import { useEffect } from 'react';
import { type BlockerFunction, useBlocker } from 'react-router-dom';

/**
 * docs/05-ADMIN-DASHBOARD.md §1 — "Unsaved-changes guard on every form
 * (`beforeunload` + a router blocker)."
 *
 * Both, because they catch different things and neither catches the other's.
 * `beforeunload` covers closing the tab, reloading, and following a link out of
 * the app; the router blocker covers clicking anything in the sidebar, which
 * never touches the browser's navigation at all.
 *
 * The browser will not let us word the `beforeunload` prompt — every engine
 * shows its own text and ignores ours. The router blocker is the one that can
 * say something useful, so the message lives with the caller's dialog.
 */
export interface UnsavedChanges {
  /** True while a navigation is being held. */
  blocking: boolean;
  /** Let it through — the caller has saved or the editor chose to discard. */
  proceed: () => void;
  /** Stay put. */
  cancel: () => void;
}

export function useUnsavedChanges(dirty: boolean): UnsavedChanges {
  useEffect(() => {
    if (!dirty) {
      return;
    }

    function onBeforeUnload(event: BeforeUnloadEvent) {
      /*
       * `preventDefault()` is the modern form; `returnValue` is what older
       * Safari and Firefox still read. Both, because the cost of the redundant
       * one is nothing and the cost of missing one is an editor losing an
       * afternoon's work to a stray Cmd-W.
       */
      event.preventDefault();
      event.returnValue = '';
    }

    window.addEventListener('beforeunload', onBeforeUnload);

    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const shouldBlock: BlockerFunction = ({ currentLocation, nextLocation }) =>
    // Not for a change of query string on the same screen: list filters and
    // `?modal=` write to the URL, and blocking those would make the guard fire
    // on actions that cannot lose anything.
    dirty && currentLocation.pathname !== nextLocation.pathname;

  const blocker = useBlocker(shouldBlock);

  return {
    blocking: blocker.state === 'blocked',
    proceed: () => blocker.proceed?.(),
    cancel: () => blocker.reset?.(),
  };
}
